import { Bug, Download, Wrench, Zap } from 'lucide-react'
import OpenClawLandingPage from '../components/openclaw/OpenClawLandingPage'
import Baidu2DownloadButtons from '../components/openclaw/Baidu2DownloadButtons'
import useBaidu2Tracking from '../hooks/useBaidu2Tracking'

function OpenClawInstall() {
  const { bdVid, landingUrl } = useBaidu2Tracking('install')

  return (
    <OpenClawLandingPage
      downloadContent={<Baidu2DownloadButtons theme="emerald" bdVid={bdVid} landingUrl={landingUrl} landingPage="install" />}
      theme="emerald"
      source="openclaw-install"
      seo={{
        title: 'OpenClaw安装教程 一步到位',
        description: '提供 OpenClaw 下载入口与安装步骤说明，帮助你快速完成 OpenClaw 安装，并附带常见问题排查与配置建议。',
        keywords: 'OpenClaw安装, OpenClaw下载, OpenClaw安装教程, OpenClaw配置, OpenClaw常见问题'
      }}
      hero={{
        eyebrow: 'OpenClaw 下载与安装',
        title: 'OpenClaw 安装教程',
        subtitle: '下载 → 安装 → 启动检查 → 常见问题排查',
        description:
          '想快速装好 OpenClaw 并开始用？这个页面按“实操步骤”整理，从版本选择到首次启动检查，按顺序走更省心。\n遇到下载失败、双击无反应或启动报错，也可以直接在页面里按排查清单逐项处理。',
        leadBullets: ['提供官方下载入口（复用主站下载逻辑）', '把安装步骤拆成关键动作，减少信息跳跃', '常见报错优先给出检查顺序与建议'],
        tags: ['下载入口', '安装步骤', '问题处理', '配置建议'],
        guideTitle: '先下载，再安装，再做简单检查',
        guideDescription: '对大多数普通用户来说，安装 OpenClaw 并不应该是复杂流程。只要先确认系统版本，再通过正确入口下载安装，然后完成最基本的启动检查即可。',
        guideBullets: [
          '优先选择与你设备匹配的系统版本，避免装错包。',
          '安装完成后首次启动时，留意是否有权限或系统提示。',
          '如果出现打不开、下载失败或启动异常，可先按常见问题区逐项排查。'
        ]
      }}
      screenshotCaption="OpenClaw 安装完成后，你通常会先看到桌面客户端主界面。建议首次启动时先熟悉入口布局，再继续下一步配置。"
      highlights={[
        {
          icon: Download,
          title: '下载入口集中',
          description: '无需自己到处找版本号。页面底部直接复用主站下载逻辑，便于用户快速选择系统版本。'
        },
        {
          icon: Wrench,
          title: '步骤清晰',
          description: '内容会按“准备、安装、启动、排查”去组织，尽量减少因为信息跳跃造成的安装失败。'
        },
        {
          icon: Bug,
          title: '带常见问题处理',
          description: '不仅告诉你怎么下载，还会告诉你遇到安装报错、权限问题、首次启动异常时先检查什么。'
        }
      ]}
      steps={[
        {
          title: '选择正确的系统版本',
          description: 'Windows 用户下载 Windows 版本，Mac 用户按 Intel 或 Apple Silicon 芯片类型下载对应版本。装错版本往往是第一类常见问题。'
        },
        {
          title: '按系统提示完成安装',
          description: '下载安装包后按系统流程安装。如果系统提示安全权限、未知来源或运行授权，先阅读提示并完成必要确认。'
        },
        {
          title: '首次启动后先确认界面与基础功能',
          description: '如果能正常打开主界面，说明安装路径基本完成。此时建议优先熟悉客户端结构，不必一开始就做复杂操作。'
        },
        {
          title: '遇到问题按排查清单处理',
          description: '若下载失败、双击无反应、启动报错或权限阻止，先回到常见问题区逐项检查，通常能快速定位。'
        }
      ]}
      sections={[
        {
          title: '安装前建议先确认这些信息',
          description: '很多“OpenClaw 安装失败”的问题，其实不是软件本身，而是设备环境、版本选择或系统拦截引起的。',
          items: [
            '确认自己是 Windows 还是 Mac 设备。',
            'Mac 用户进一步确认是 Intel 芯片还是 Apple Silicon 芯片。',
            '预留稳定的下载环境，避免中途中断造成安装包损坏。',
            '首次安装时尽量关闭不必要的系统干扰项，方便判断是否能正常启动。'
          ]
        },
        {
          title: '安装完成后怎么判断是否“装好了”',
          description: '普通用户最关心的不是复杂技术指标，而是“我现在能不能开始用了”。所以判断标准要足够直观。',
          items: [
            '客户端可以正常打开，不闪退。',
            '主界面可以看到基本功能入口。',
            '下载区和界面资源显示正常，没有明显缺失。',
            '可以继续进入后续使用或功能体验流程。'
          ]
        }
      ]}
      faq={[
        {
          q: 'OpenClaw 安装包下载后打不开怎么办？',
          a: '先确认是否下载了正确系统版本，再检查文件是否完整。如果是系统拦截或权限提示，可先按系统要求完成允许操作。'
        },
        {
          q: 'Mac 用户怎么判断该下载哪一个版本？',
          a: '如果你的 Mac 是 Intel 芯片，就选 Mac Intel 版本；如果是 M 系列芯片，一般选择 Mac Apple 版本。'
        },
        {
          q: '安装成功后还需要复杂配置吗？',
          a: '这个页面会优先按普通用户思路给出最短路径。建议先完成基础启动与界面确认，再考虑后续更深入的配置。'
        },
        {
          q: '如果安装好了但不知道下一步做什么？',
          a: '先从主界面开始熟悉入口，然后再根据你的用途去尝试 OpenClaw 的基本功能与引导内容。'
        }
      ]}
      closing={{
        title: '现在就下载并按步骤完成安装',
        description: '如果你的目标是尽快装好并开始体验 OpenClaw，直接从下方选择系统版本即可。下载后按页面步骤操作，通常可以顺利完成安装。',
        footerTitle: '下载、安装、排查，一页完成',
        footerDescription: '相比把下载链接、安装说明和问题处理拆散在多个页面，这里更适合直接承接百度广告流量，让用户一步一步完成安装。',
        footerTags: ['OpenClaw下载安装', '系统版本选择', '安装步骤说明', '常见问题排查']
      }}
    />
  )
}

export default OpenClawInstall
